import {Component, Input, OnInit, TemplateRef, ViewChild} from '@angular/core';
import {AdminValetService} from '../../../../admin/modules/admin-valet/services/admin-valet.service';
import {TranslateService} from '@ngx-translate/core';
import { GenericDatasource } from 'src/app/shared/datasource/generic.datasource';
import { combineLatest, Subject } from 'rxjs';
import { ColumnSettingsItem } from 'src/app/shared/components/shared-list/column-settings/column-settings.component';
import { ButtonsConfig } from 'src/app/shared/models/button.interface';
import { FilterConfig, FilterData } from 'src/app/shared/models/filter-config.interface';
import { GridColumnConfig } from 'src/app/shared/models/grid-config.interface';
import { LinkDisplayColumnComponent } from 'src/app/shared/components/link-display-column/link-display-column.component';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonValetInvoicesConfig } from '../../config/common-valet-invoices.config';
import { takeUntil, skip } from 'rxjs/operators';

@Component({
    selector: 'pv-common-valet-invoices',
    templateUrl: './common-valet-invoices.component.html',
    styleUrls: ['./common-valet-invoices.component.scss'],
    standalone: false
})
export class CommonValetInvoicesComponent extends GenericDatasource<any> implements OnInit {

  private readonly FILTERS_STORAGE_KEY = 'commonValetInvoiceFilters';
  private readonly COLUMN_SETTINGS_STORAGE_KEY = 'commonValetInvoiceColumnsSettings';
  private readonly COLUMNS_SORT_STORAGE_KEY = 'commonValetInvoiceSort';

  @Input() valetId: string;

  public isDataSourceLoading = true;
  public columns: GridColumnConfig[] = [];
  public columnSettings: ColumnSettingsItem[] = [];
  public showColumnSettings = true;
  
  private sub: any;
  private destroy$: Subject<void> = new Subject<void>();

  public buttonsConfig: ButtonsConfig = {
    exportAll: { show: false, isLoading: false },
    export: { show: true, isLoading: false },
    refresh: { show: true, isLoading: false }
  };
  
  public get isLoading(): boolean {
    return this.isDataSourceLoading;
  }

  public filtersConfig: FilterConfig[] = [];
  public showClearFiltersButton: boolean = false;
  public initialFilterValues: FilterData = {};

  @ViewChild('dateTemplate', { static: true }) dateTemplate: TemplateRef<LinkDisplayColumnComponent>;
  @ViewChild('amountTemplate', { static: true }) amountTemplate: TemplateRef<LinkDisplayColumnComponent>;
  @ViewChild('fileTemplate', { static: true }) fileTemplate: TemplateRef<LinkDisplayColumnComponent>;

  public pdfBtnText = this.translateService.instant('modules.valet.valet_invoices.grid.open');

  constructor(private adminValetService: AdminValetService,
              private activeRoute: ActivatedRoute,
              private router: Router,
              private commonValetInvoicesConfig: CommonValetInvoicesConfig,
              private translateService: TranslateService) {
    super();
  }

  ngOnInit(): void {
    const config = this.commonValetInvoicesConfig.createGridConfig(
      this.FILTERS_STORAGE_KEY,
      this.COLUMNS_SORT_STORAGE_KEY,
      this.adminValetService,
      this.router,
      this.activeRoute,
      this.valetId
    );
    this.setFilter('valetId', this.valetId);
    this.initialize(config);

    this.initializeColumns();
    this.setupColumnSettings();
    this.handleRouteParams();
    this.setupDataSource();

  }

  private handleRouteParams() {
    this.activeRoute.queryParams
      .pipe(takeUntil(this.destroy$), skip(1))
      .subscribe(() => {});
  }

  get valetId$() { 
    return this.getFilter('valetId'); 
  }

  private initializeColumns() {
    this.columns = this.setColumns();
  }

  public setColumns() {
    return this.commonValetInvoicesConfig.getCommonValetInvoicesColumnConfig(
      this.dateTemplate,
      this.amountTemplate,
      this.fileTemplate);
  }

  private setupColumnSettings() {
    this.columnSettings = this.initializeColumnSettings(
      this.columns,
      this.COLUMN_SETTINGS_STORAGE_KEY,
      ['date', 'invoiceReference']
    );
    this.applyColumnVisibility();
  }

  public onColumnVisibilityChanged(columnSettings: ColumnSettingsItem[]) {
    this.columnSettings = columnSettings;
    localStorage.setItem(this.COLUMN_SETTINGS_STORAGE_KEY, JSON.stringify(columnSettings));
    this.applyColumnVisibility();
  }

  private applyColumnVisibility() {
    const allColumns = this.setColumns();
    this.columns = allColumns.filter(column => {
      const setting = this.columnSettings.find(s => s.key === column.key);
      return setting ? setting.visible : true;
    });
  }

  private setupDataSource() {
    combineLatest([
      this.connect(null),
      this.loading$
    ])
    .pipe(takeUntil(this.destroy$))
    .subscribe(([data, isLoading]) => {
      this.isDataSourceLoading = isLoading;
    });
  }

  public redirectToPdf = (row: any): void => {
    window.open(row.file.url, '_blank');
  }

  public onExportPageData(): void {
    this.exportCurrentPageData(this.buttonsConfig);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    
    if (this.sub) {
      this.sub.unsubscribe();
    }
  }
}
